<template>
  <div class="container mx-auto px-4 pt-40 pb-32 max-w-4xl">
    <div class="flex items-center mb-8">
      <UButton icon="i-lucide-arrow-left" variant="ghost" class="mr-4" to="/">
        Retour
      </UButton>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Mon Panier
      </h1>
    </div>

    <div v-if="cartStore.items.length === 0" class="text-center py-16">
      <UIcon
        name="i-lucide-shopping-cart"
        class="w-24 h-24 mx-auto text-gray-300 mb-6"
      />
      <h2 class="text-xl font-medium text-gray-600 mb-4">
        Votre panier est vide
      </h2>
      <UButton to="/" color="main" size="lg"> Découvrir la carte </UButton>
    </div>

    <div v-else>
      <!-- Stepper -->
      <div class="flex justify-center mb-12">
        <div class="flex items-center">
          <template v-for="(step, index) in steps" :key="step.id">
            <div
              class="flex flex-col items-center relative group cursor-pointer"
              @click="goToStep(step.id)"
            >
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm z-10 transition-colors"
                :class="
                  currentStep >= step.id
                    ? 'bg-main text-white'
                    : 'bg-gray-200 text-gray-500'
                "
              >
                <UIcon
                  v-if="currentStep > step.id"
                  name="i-lucide-check"
                  class="w-5 h-5"
                />
                <span v-else>{{ step.id }}</span>
              </div>
              <span
                class="absolute -bottom-6 whitespace-nowrap text-xs font-semibold"
                :class="currentStep >= step.id ? 'text-main' : 'text-gray-500'"
                >{{ step.label }}</span
              >
            </div>
            <div
              v-if="index < steps.length - 1"
              class="w-16 md:w-32 h-1 mx-2 rounded-full"
              :class="currentStep > step.id ? 'bg-main' : 'bg-gray-200'"
            />
          </template>
        </div>
      </div>

      <!-- Step 1: Panier -->
      <div v-if="currentStep === 1" class="space-y-8">
        <!-- Loop through grouped items -->
        <div
          v-for="group in groupedItems"
          :key="group.name"
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
        >
          <div
            class="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-100 dark:border-gray-700"
          >
            <h2 class="text-xl font-semibold text-main-color">
              {{ group.name }}
            </h2>
          </div>

          <div class="divide-y divide-gray-100 dark:divide-gray-700">
            <div
              v-for="item in group.items"
              :key="item.id"
              class="p-6 flex flex-col md:flex-row items-start md:items-center gap-6"
            >
              <!-- Image -->
              <NuxtImg
                :src="getImageUrl((item.meal || item.menu)?.image)"
                :alt="(item.meal || item.menu)?.name"
                class="w-24 h-24 rounded-lg object-cover flex-shrink-0 bg-gray-100"
              />

              <!-- Details -->
              <div class="flex-1 min-w-0 w-full">
                <div class="flex justify-between items-start mb-2">
                  <h3
                    class="text-lg font-medium text-gray-900 dark:text-white truncate pr-4"
                  >
                    {{ (item.meal || item.menu)?.name }}
                  </h3>
                  <span
                    class="text-lg font-semibold text-main-color whitespace-nowrap"
                  >
                    {{ item.totalPrice.toFixed(2) }}€
                  </span>
                </div>

                <!-- Content/Ingredients Summary -->
                <div class="mt-3 text-sm text-gray-500 mb-4">
                  <!-- Meal Ingredients (Regular/Customized) -->
                  <template v-if="item.meal">
                    <div class="mt-2 space-y-1">
                      <div
                        v-for="disp in getUnifiedDisplayIngredients(
                          item.meal,
                          item.extras,
                        )"
                        :key="disp.name"
                        class="flex justify-between text-sm text-gray-600 dark:text-gray-400"
                      >
                        <span
                          v-if="disp.currentQty === 0 && disp.baseQty > 0"
                          class="text-red-500 font-medium"
                        >
                          Sans {{ disp.name }}
                        </span>
                        <span v-else>
                          {{ disp.name }}
                          {{ formatIngredientQuantity(disp) }}
                        </span>
                        <span
                          v-if="!(disp.currentQty === 0 && disp.baseQty > 0)"
                          :class="{
                            'text-gray-400': disp.totalExtraCost === 0,
                            'text-main-color font-medium':
                              disp.totalExtraCost > 0,
                          }"
                        >
                          {{
                            disp.totalExtraCost > 0
                              ? `+${disp.totalExtraCost.toFixed(2)}€`
                              : "Inclus"
                          }}
                        </span>
                      </div>
                    </div>
                  </template>

                  <!-- Menu Content -->
                  <template v-if="item.type === 'menu' && item.menu">
                    <div class="mb-3">
                      <p
                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Contenu du menu :
                      </p>
                      <ul
                        class="text-sm text-gray-600 dark:text-gray-400 space-y-2"
                      >
                        <li
                          v-for="content in item.menu.menu_content"
                          :key="content.id"
                        >
                          <div class="flex justify-between">
                            <span class="font-medium">{{
                              content.repas.name
                            }}</span>
                          </div>
                          <!-- Show ingredients for this meal -->
                          <div
                            class="pl-2 mt-1 border-l-2 border-gray-200 space-y-1"
                          >
                            <div
                              v-for="disp in getUnifiedDisplayIngredients(
                                content.repas,
                                item.menuCustomizations?.find(
                                  (c) => c.menuContentId === content.id,
                                )?.extras,
                              )"
                              :key="disp.name"
                              class="flex justify-between text-xs text-gray-600 dark:text-gray-400"
                            >
                              <span
                                v-if="disp.currentQty === 0 && disp.baseQty > 0"
                                class="text-red-500 font-medium"
                              >
                                Sans {{ disp.name }}
                              </span>
                              <span v-else>
                                {{ disp.name }}
                                {{ formatIngredientQuantity(disp) }}
                              </span>
                              <span
                                v-if="
                                  !(disp.currentQty === 0 && disp.baseQty > 0)
                                "
                                :class="{
                                  'text-gray-400': disp.totalExtraCost === 0,
                                  'text-main-color font-medium':
                                    disp.totalExtraCost > 0,
                                }"
                              >
                                {{
                                  disp.totalExtraCost > 0
                                    ? `+${disp.totalExtraCost.toFixed(2)}€`
                                    : "Inclus"
                                }}
                              </span>
                            </div>
                          </div>
                        </li>
                      </ul>
                      <!-- Selected Sauces -->
                      <div
                        v-if="
                          item.selectedSauces && item.selectedSauces.length > 0
                        "
                        class="mt-2"
                      >
                        <span
                          class="text-xs font-semibold text-gray-500 uppercase tracking-wider"
                          >Sauces :</span
                        >
                        <div class="flex flex-wrap gap-1 mt-1">
                          <UBadge
                            v-for="s in item.selectedSauces"
                            :key="s.sauce.id"
                            color="orange"
                            variant="subtle"
                            size="sm"
                          >
                            {{ s.quantity > 1 ? `${s.quantity}x ` : ""
                            }}{{ s.sauce.name }}
                          </UBadge>
                        </div>
                      </div>

                      <!-- Selected Desserts -->
                      <div
                        v-if="
                          item.selectedDesserts &&
                          item.selectedDesserts.length > 0
                        "
                        class="mt-2"
                      >
                        <span
                          class="text-xs font-semibold text-gray-500 uppercase tracking-wider"
                          >Desserts :</span
                        >
                        <div class="flex flex-wrap gap-1 mt-1">
                          <UBadge
                            v-for="d in item.selectedDesserts"
                            :key="d.dessert.id"
                            color="pink"
                            variant="subtle"
                            size="sm"
                          >
                            {{ d.quantity > 1 ? `${d.quantity}x ` : ""
                            }}{{ d.dessert.name }}
                          </UBadge>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Note Input -->
                <div
                  v-if="group.name !== 'Boissons' && group.name !== 'Desserts'"
                  class="mb-4"
                >
                  <UInput
                    v-model="item.note"
                    placeholder="Ajouter une note"
                    icon="i-lucide-sticky-note"
                    size="sm"
                    :maxlength="100"
                    color="gray"
                    variant="outline"
                    class="w-full"
                  />
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-between mt-auto">
                  <div class="flex items-center gap-3">
                    <UButton
                      color="gray"
                      variant="soft"
                      icon="i-lucide-minus"
                      size="xs"
                      :disabled="item.quantity <= 1"
                      @click="updateQuantity(item, -1)"
                    />
                    <span class="font-medium w-6 text-center">{{
                      item.quantity
                    }}</span>
                    <UButton
                      color="gray"
                      variant="soft"
                      icon="i-lucide-plus"
                      size="xs"
                      @click="updateQuantity(item, 1)"
                    />
                  </div>

                  <div class="flex items-center gap-2">
                    <UButton
                      v-if="
                        group.name !== 'Boissons' && group.name !== 'Desserts'
                      "
                      color="gray"
                      variant="ghost"
                      icon="i-lucide-pencil"
                      size="sm"
                      @click="editItem(item)"
                    />
                    <UButton
                      color="red"
                      variant="ghost"
                      icon="i-lucide-trash-2"
                      label="Supprimer"
                      size="sm"
                      @click="cartStore.removeItem(item.id)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Total & Checkout -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-8 sticky bottom-4"
        >
          <div class="flex justify-between items-center mb-6">
            <span class="text-xl font-medium text-gray-600 dark:text-gray-300"
              >Total</span
            >
            <span class="text-3xl font-bold text-main-color"
              >{{ cartStore.totalPrice.toFixed(2) }}€</span
            >
          </div>
          <UButton block size="xl" color="main" @click="goToStep(2)">
            Valider mon panier
          </UButton>
        </div>
      </div>

      <!-- Step 2: Livraison -->
      <div v-if="currentStep === 2" class="max-w-2xl mx-auto">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 md:p-8">
          <h2 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            Détails de livraison
          </h2>

          <!-- Saved Addresses Selection -->
          <div
            v-if="savedAddresses.length > 0"
            class="mb-8 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex justify-between items-center mb-3">
              <h3 class="font-semibold text-gray-900 dark:text-white">
                Mes adresses enregistrées
              </h3>
              <UButton
                variant="ghost"
                color="gray"
                size="xs"
                label="Nouvelle adresse"
                icon="i-lucide-plus"
                @click="selectedAddressId = null"
              />
            </div>

            <div class="space-y-3">
              <div
                v-for="item in savedAddresses"
                :key="item.id"
                class="flex items-start gap-3 p-3 rounded-md cursor-pointer border transition-colors"
                :class="
                  selectedAddressId === item.id
                    ? 'border-main-color bg-main-color/5 dark:bg-main-color/10 ring-1 ring-main-color'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                "
                @click="selectedAddressId = item.id"
              >
                <div class="mt-0.5">
                  <div
                    class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors"
                    :class="
                      selectedAddressId === item.id
                        ? 'border-main-color'
                        : 'border-gray-400'
                    "
                  >
                    <div
                      v-if="selectedAddressId === item.id"
                      class="w-2 h-2 rounded-full bg-main-color"
                    />
                  </div>
                </div>
                <div>
                  <p class="font-medium text-sm text-gray-900 dark:text-white">
                    {{ item.address.address_1 }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ item.address.zip_code }} {{ item.address.city }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <UForm
            ref="form"
            :schema="deliverySchema"
            :state="deliveryDetails"
            class="space-y-6"
            @submit="goToStep(3)"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UFormField label="Prénom" name="firstName" required>
                <UInput
                  v-model="deliveryDetails.firstName"
                  placeholder="Votre prénom"
                  size="lg"
                  class="w-full"
                  color="main"
                  :ui="{
                    color: {
                      gray: {
                        outline:
                          'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                      },
                    },
                  }"
                />
              </UFormField>
              <UFormField label="Nom" name="lastName" required>
                <UInput
                  v-model="deliveryDetails.lastName"
                  placeholder="Votre nom"
                  size="lg"
                  class="w-full"
                  color="main"
                  :ui="{
                    color: {
                      gray: {
                        outline:
                          'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                      },
                    },
                  }"
                />
              </UFormField>
            </div>

            <UFormField label="Numéro de téléphone" name="phone" required>
              <UInput
                v-model="deliveryDetails.phone"
                placeholder="06 12 34 56 78"
                icon="i-lucide-phone"
                size="lg"
                class="w-full"
                color="main"
                :ui="{
                  color: {
                    gray: {
                      outline:
                        'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                    },
                  },
                }"
              />
            </UFormField>

            <UFormField label="Adresse (Numéro et voie)" name="street" required>
              <UInput
                v-model="deliveryDetails.street"
                placeholder="123 Rue de la Paix"
                icon="i-lucide-map-pin"
                size="lg"
                class="w-full"
                color="main"
                :ui="{
                  color: {
                    gray: {
                      outline:
                        'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                    },
                  },
                }"
              />
            </UFormField>

            <div class="grid grid-cols-2 gap-4">
              <UFormField label="Code Postal" name="postalCode" required>
                <UInput
                  v-model="deliveryDetails.postalCode"
                  placeholder="75000"
                  size="lg"
                  class="w-full"
                  color="main"
                  :ui="{
                    color: {
                      gray: {
                        outline:
                          'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                      },
                    },
                  }"
                />
              </UFormField>
              <UFormField
                label="Ville"
                name="city"
                required
                :error="calculationError"
              >
                <USelectMenu
                  v-model="selectedCityObject"
                  :items="availableCities"
                  searchable
                  :search-attributes="['label']"
                  placeholder="Rechercher une ville..."
                  option-attribute="label"
                  by="uuid"
                  size="lg"
                  class="w-full"
                  :ui="{
                    color: {
                      gray: {
                        outline:
                          'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                      },
                    },
                  }"
                >
                  <template #label>
                    <span v-if="selectedCityObject" class="truncate">{{
                      selectedCityObject.label
                    }}</span>
                    <span v-else class="text-gray-500"
                      >Rechercher une ville...</span
                    >
                  </template>
                </USelectMenu>
              </UFormField>
            </div>

            <UFormField
              label="Instructions complémentaires"
              name="instructions"
            >
              <UTextarea
                v-model="deliveryDetails.instructions"
                placeholder="Digicode, interphone, etc..."
                :rows="2"
                size="lg"
                class="w-full"
                color="main"
                :ui="{
                  color: {
                    gray: {
                      outline:
                        'bg-white dark:bg-white text-gray-900 dark:text-gray-900 ring-gray-300 dark:ring-gray-300 focus:ring-main-color dark:focus:ring-main-color',
                    },
                  },
                }"
              />
            </UFormField>

            <div class="flex gap-4 pt-4">
              <UButton
                color="gray"
                variant="ghost"
                size="lg"
                @click="goToStep(1)"
              >
                Retour
              </UButton>
              <UButton
                type="submit"
                color="main"
                size="lg"
                block
                class="flex-1"
              >
                Vers le paiement
              </UButton>
            </div>
          </UForm>
        </div>
      </div>

      <!-- Step 3: Paiement & Récapitulatif -->
      <div v-if="currentStep === 3" class="max-w-2xl mx-auto space-y-6">
        <!-- Address Recap -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700"
        >
          <div class="flex justify-between items-start mb-4">
            <h3 class="font-semibold text-lg">Livraison</h3>
            <UButton color="main" variant="link" size="sm" @click="goToStep(2)"
              >Modifier</UButton
            >
          </div>
          <div class="text-gray-600 dark:text-gray-300">
            <p class="font-medium text-gray-900 dark:text-white">
              {{ deliveryDetails.firstName }} {{ deliveryDetails.lastName }}
            </p>
            <p>
              {{ deliveryDetails.street }}<br />
              {{ deliveryDetails.postalCode }} {{ deliveryDetails.city }}
            </p>
            <p>{{ deliveryDetails.phone }}</p>
            <p v-if="deliveryDetails.instructions" class="text-sm italic mt-1">
              Note: {{ deliveryDetails.instructions }}
            </p>
          </div>
        </div>

        <!-- Order Summary Condensed -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
        >
          <div
            class="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center"
          >
            <h3 class="font-semibold text-lg">
              Commande ({{ cartStore.items.length }} articles)
            </h3>
            <UButton color="main" variant="link" size="sm" @click="goToStep(1)"
              >Modifier</UButton
            >
          </div>
          <div class="p-6 space-y-4">
            <div
              v-for="item in cartStore.items"
              :key="item.id"
              class="flex justify-between items-start text-sm"
            >
              <div>
                <span class="font-medium"
                  >{{ item.quantity }}x
                  {{ (item.meal || item.menu)?.name }}</span
                >
                <p v-if="item.note" class="text-xs text-gray-500 italic">
                  Note: {{ item.note }}
                </p>
              </div>
              <span>{{ item.totalPrice.toFixed(2) }}€</span>
            </div>
          </div>
        </div>

        <!-- Final Total & Payment Form -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div class="space-y-3 mb-6">
            <div class="flex justify-between text-gray-600 dark:text-gray-300">
              <span>Sous-total</span>
              <span>{{ cartStore.totalPrice.toFixed(2) }}€</span>
            </div>
            <div class="flex justify-between text-gray-600 dark:text-gray-300">
              <span>Frais de livraison</span>
              <span>{{ deliveryFee.toFixed(2) }}€</span>
            </div>
            <div class="border-t border-gray-200 dark:border-gray-700 my-2" />
            <div class="flex justify-between items-center text-xl font-bold">
              <span>Total à payer</span>
              <span class="text-main-color">{{ finalTotal.toFixed(2) }}€</span>
            </div>
          </div>

          <div v-if="!clientSecret" class="flex justify-center py-4">
            <UIcon
              name="i-lucide-loader-2"
              class="animate-spin w-8 h-8 text-main"
            />
          </div>

          <PaymentForm v-else :client-secret="clientSecret" />
        </div>
      </div>
    </div>
    <!-- Edit Drawers -->
    <MealCustomizeDrawer
      v-if="currentMealToEdit"
      v-model="isMealEditOpen"
      :meal="currentMealToEdit"
      :existing-extras="existingExtrasOfEdit"
      :auto-add-to-cart="false"
      @meal-customized="handleMealEdited"
    />

    <MenuCustomizeDrawer
      v-if="currentMenuToEdit"
      v-model="isMenuEditOpen"
      :menu="currentMenuToEdit"
      :existing-customizations="existingMenuCustomizationsOfEdit"
      :existing-sauces="existingSaucesOfEdit"
      :existing-desserts="existingDessertsOfEdit"
      :auto-add-to-cart="false"
      @menu-customized="handleMenuEdited"
    />
  </div>
</template>

<script setup lang="ts">
import { z } from "zod";

const cartStore = useCartStore();
const config = useRuntimeConfig();
const { user, isLoggedIn } = useAuth();
const { $api } = useNuxtApp();

const getImageUrl = (path: string | undefined) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${config.public.apiUrl}${path}`;
};

const savedAddresses = ref<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any
const selectedAddressId = ref<number | null>(null);

const fetchUserAddresses = async () => {
  if (!isLoggedIn.value) return;
  try {
    savedAddresses.value = await $api.get<any[]>("/user-addresses", "include"); // eslint-disable-line @typescript-eslint/no-explicit-any
  } catch (e) {
    console.error("Failed to fetch addresses", e);
  }
};

onMounted(() => {
  if (isLoggedIn.value) {
    fetchUserAddresses();
  }
});

// Wizard State
const currentStep = ref(1);
const steps = [
  { id: 1, label: "Panier" },
  { id: 2, label: "Livraison" },
  { id: 3, label: "Paiement" },
];

watch(currentStep, (newStep) => {
  if (newStep === 2 && isLoggedIn.value && savedAddresses.value.length === 0) {
    fetchUserAddresses();
  }
});

const deliveryDetails = reactive({
  firstName: "",
  lastName: "",
  street: "",
  postalCode: "",
  city: "",
  phone: "",
  instructions: "",
});

const deliverySchema = z.object({
  firstName: z.string().min(2, "Le prénom est requis"),
  lastName: z.string().min(2, "Le nom est requis"),
  street: z.string().min(5, "L'adresse est requise"),
  postalCode: z.string().regex(/^\d{5}$/, "Code postal invalide (5 chiffres)"),
  city: z.string().min(2, "La ville est requise"),
  phone: z
    .string()
    .regex(
      /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
      "Numéro de téléphone invalide",
    ),
  instructions: z.string().optional(),
});

const deliveryFee = ref(0);
const finalTotal = computed(() => cartStore.totalPrice + deliveryFee.value);
const form = ref(); // Reference to the form
const clientSecret = ref("");

// const availableCities = ref<string[]>([]); // Removed unused
const isCalculated = ref(false);
const calculationError = ref("");
// Store cities for the dropdown
// Use useFetch for data fetching
const { data: rawCities, error: citiesError } = await useFetch<
  Array<{
    city: string;
    postalCode?: string;
    uuid: string;
  }>
>(`${config.public.apiUrl || "http://localhost:4200/api"}/delivery/cities`);

const availableCities = computed(() => {
  const cities = rawCities.value || [];
  return cities.map((c) => ({
    label: c.postalCode ? `${c.city} (${c.postalCode})` : c.city,
    value: c.city,
    uuid: c.uuid,
    postalCode: c.postalCode,
  }));
});

if (import.meta.client && citiesError.value) {
  console.error("Cities Error:", citiesError.value);
}

// Search function no longer needed for USelectMenu if using options prop,
// but USelectMenu with 'searchable' can still be used if we want server-side search.
// However, for small list, client-side filtering (default behavior of USelectMenu with options) is better.

const selectedCityObject = ref<{
  label: string;
  value: string;
  uuid: string;
  postalCode?: string;
} | null>(null);

// Update deliveryDetails when city object changes
watch(selectedCityObject, async (val) => {
  if (val) {
    deliveryDetails.city = val.value;
    // Auto-fill postal code if available and different
    if (val.postalCode && deliveryDetails.postalCode !== val.postalCode) {
      deliveryDetails.postalCode = val.postalCode;
    }

    // Calculate Fee
    try {
      const result = await $fetch<{
        eligible: boolean;
        fee: number;
        reason?: string;
      }>(
        `${
          config.public.apiUrl || "http://localhost:4200/api"
        }/delivery/calculate`,
        {
          method: "POST",
          body: {
            cityUuid: val.uuid,
            cartTotal: cartStore.totalPrice,
          },
        },
      );

      if (result.eligible) {
        deliveryFee.value = result.fee;
        isCalculated.value = true;
        calculationError.value = "";
      } else {
        deliveryFee.value = 0;
        isCalculated.value = false;
        calculationError.value = result.reason || "Livraison impossible";
      }
    } catch {
      deliveryFee.value = 0;
      isCalculated.value = false;
      calculationError.value = "Erreur de calcul";
    }
  } else {
    deliveryDetails.city = "";
    deliveryFee.value = 0;
    isCalculated.value = false;
    calculationError.value = "";
  }
});

// Watch Selected Address to auto-fill
watch(selectedAddressId, (newId) => {
  if (newId) {
    const item = savedAddresses.value.find((a) => a.id === newId);
    if (item && item.address) {
      const addr = item.address;

      if (user.value) {
        deliveryDetails.firstName = user.value.first_name || "";
        deliveryDetails.lastName = user.value.last_name || "";
        deliveryDetails.phone = user.value.phone_number || "";
      }
      deliveryDetails.street = addr.address_1;
      if (addr.address_2) deliveryDetails.street += " " + addr.address_2;
      deliveryDetails.postalCode = addr.zip_code;

      // Try to match city
      const foundCity = availableCities.value.find(
        (c) =>
          c.value.toLowerCase() === addr.city.toLowerCase() ||
          (c.postalCode && c.postalCode === addr.zip_code),
      );

      if (foundCity) {
        selectedCityObject.value = foundCity;
      } else {
        // Fallback
        deliveryDetails.city = addr.city;
      }
    }
  }
});

// Watch Postal Code to auto-select city
watch(
  () => deliveryDetails.postalCode,
  (newZip) => {
    if (!newZip || newZip.length < 5) return;

    // Find city with matching postal code
    // If selectedCityObject is already set and matches, do nothing to avoid loop/reset
    if (
      selectedCityObject.value &&
      selectedCityObject.value.postalCode === newZip
    ) {
      return;
    }

    const found = availableCities.value.find((c) => c.postalCode === newZip);
    if (found) {
      selectedCityObject.value = found;
    }
  },
);

// Re-calculate if cart total changes
watch(
  () => cartStore.totalPrice,
  async () => {
    if (selectedCityObject.value) {
      // Retrigger calculation
      const val = selectedCityObject.value;
      try {
        const result = await $fetch<{
          eligible: boolean;
          fee: number;
          reason?: string;
        }>(
          `${
            config.public.apiUrl || "http://localhost:4200/api"
          }/delivery/calculate`,
          {
            method: "POST",
            body: {
              cityUuid: val.uuid,
              cartTotal: cartStore.totalPrice,
            },
          },
        );

        if (result.eligible) {
          deliveryFee.value = result.fee;
          isCalculated.value = true;
          calculationError.value = "";
        } else {
          isCalculated.value = false;
          calculationError.value = result.reason || "Livraison impossible";
        }
      } catch {
        /* silent */
      }
    }
  },
);

// validateStep2 removed as it was unused and logic is inside goToStep

const goToStep = async (step: number) => {
  if (step < currentStep.value) {
    currentStep.value = step;
  } else if (step === 2 && currentStep.value === 1) {
    currentStep.value = 2;
  } else if (step === 3 && currentStep.value === 2) {
    // Validate Step 2 using UForm
    try {
      if (form.value) {
        await form.value.validate();

        // Extra check for delivery eligibility
        if (!isCalculated.value) {
          alert(
            calculationError.value ||
              "Livraison impossible pour cette commande (Montant minimum non atteint ou ville non desservie)",
          );
          return;
        }

        // Fetch Payment Intent
        try {
          const { clientSecret: secret } = await $fetch<{
            clientSecret: string;
          }>(
            `${
              config.public.apiUrl || "http://localhost:4200/api"
            }/payment/create-intent`,
            {
              method: "POST",
              body: {
                amount: finalTotal.value,
                currency: "eur",
              },
            },
          );
          clientSecret.value = secret;
          currentStep.value = 3;
        } catch (e) {
          console.error("Payment intent error:", e);
          alert("Erreur lors de l'initialisation du paiement.");
        }
      }
    } catch {
      // Validation failed, errors are shown by UForm
    }
  }
};

// --- Display Logic Helper ---
interface IngredientDisplay {
  name: string;
  baseQty: number;
  addedQty: number;
  currentQty: number;
  pricePerUnit: number;
  totalExtraCost: number;
}

const parseOriginalIngredients = (
  meal: Repas,
): Record<string, { quantity: number; name: string }> => {
  const originalIngredients: Record<
    string,
    { quantity: number; name: string }
  > = {};

  // Method 1: Use structured content (Preferred)
  if (meal.content && Array.isArray(meal.content) && meal.content.length > 0) {
    meal.content.forEach(
      (item: { ingredient?: { name: string }; quantity: number }) => {
        if (item.ingredient && item.ingredient.name) {
          originalIngredients[item.ingredient.name.toLowerCase()] = {
            quantity: item.quantity,
            name: item.ingredient.name, // Keep casing
          };
        }
      },
    );
    return originalIngredients;
  }

  // Method 2: Fallback to string parsing
  if (meal.ingredients) {
    const ingredientStrings = meal.ingredients
      .split(",")
      .map((s: string) => s.trim());

    ingredientStrings.forEach((ingredientString: string) => {
      const match = ingredientString.match(/^(\d+)\s+(.+)$/);
      if (match) {
        const quantity = parseInt(match[1]);
        const ingredientName = match[2].trim();
        originalIngredients[ingredientName.toLowerCase()] = {
          quantity,
          name: ingredientName,
        };
      } else if (ingredientString.length > 0) {
        // Implicit quantity 1
        const ingredientName = ingredientString.trim();
        originalIngredients[ingredientName.toLowerCase()] = {
          quantity: 1,
          name: ingredientName,
        };
      }
    });
  }
  return originalIngredients;
};

const getUnifiedDisplayIngredients = (
  meal: Repas,
  extras: Array<{ ingredient: Ingredient; quantity: number }> | undefined,
): IngredientDisplay[] => {
  const originals = parseOriginalIngredients(meal);
  const displayMap = new Map<string, IngredientDisplay>();

  // 1. Add all originals
  Object.values(originals).forEach((orig) => {
    displayMap.set(orig.name.toLowerCase(), {
      name: orig.name,
      baseQty: orig.quantity,
      addedQty: 0,
      currentQty: orig.quantity,
      pricePerUnit: 0,
      totalExtraCost: 0,
    });
  });

  // 2. Process extras (Updates or Adds)
  if (extras) {
    extras.forEach((extra) => {
      const key = extra.ingredient.name.toLowerCase();
      const existing = displayMap.get(key);

      if (existing) {
        // Updated quantity
        const added = Math.max(0, extra.quantity - existing.baseQty);
        existing.addedQty = added;
        existing.currentQty = extra.quantity;
        existing.pricePerUnit = extra.ingredient.price;
        existing.totalExtraCost = added * extra.ingredient.price;
      } else {
        // Completely new extra
        displayMap.set(key, {
          name: extra.ingredient.name,
          baseQty: 0,
          addedQty: extra.quantity,
          currentQty: extra.quantity,
          pricePerUnit: extra.ingredient.price,
          totalExtraCost: extra.quantity * extra.ingredient.price,
        });
      }
    });
  }

  // Filter out items that are strictly 0 quantity AND 0 base quantity (ghosts)
  // But keep items that are 0 currentQty but >0 baseQty (Removed)
  return Array.from(displayMap.values()).filter(
    (d) => d.currentQty > 0 || d.baseQty > 0,
  );
};

const formatIngredientQuantity = (item: IngredientDisplay) => {
  if (item.currentQty === 0 && item.baseQty > 0) {
    return ""; // Will be handled by template name replacement
  }

  // Format: "Steak (1 + 1)" or "Steak (+1)" or "Steak (1)" based on user req
  if (item.baseQty > 0 && item.addedQty > 0) {
    return `(${item.baseQty} + ${item.addedQty})`;
  } else if (item.baseQty > 0 && item.addedQty === 0) {
    // Check if quantity reduced?
    if (item.currentQty < item.baseQty) {
      return `(${item.currentQty})`;
    }
    return `(${item.baseQty})`;
  } else if (item.baseQty === 0 && item.addedQty > 0) {
    return `(+${item.addedQty})`;
  }
  return "";
};

// Edit Logic State
const isMealEditOpen = ref(false);
const isMenuEditOpen = ref(false);
const itemToEditId = ref<string | null>(null);
const currentMealToEdit = ref<Repas | null>(null);
const existingExtrasOfEdit = ref<CartItem["extras"]>([]);
const currentMenuToEdit = ref<Menu | null>(null);
const existingMenuCustomizationsOfEdit = ref<CartItem["menuCustomizations"]>(
  [],
);
const existingSaucesOfEdit = ref<CartItem["selectedSauces"]>([]);
const existingDessertsOfEdit = ref<CartItem["selectedDesserts"]>([]);

const editItem = (item: CartItem) => {
  if (item.type === "regular" || item.type === "customized") {
    if (!item.meal) return;
    itemToEditId.value = item.id;
    currentMealToEdit.value = item.meal;
    existingExtrasOfEdit.value = item.extras || [];
    isMealEditOpen.value = true;
  } else if (item.type === "menu") {
    if (!item.menu) return;
    itemToEditId.value = item.id;
    currentMenuToEdit.value = item.menu;
    existingMenuCustomizationsOfEdit.value = item.menuCustomizations || [];
    existingSaucesOfEdit.value = item.selectedSauces || [];
    existingDessertsOfEdit.value = item.selectedDesserts || [];
    isMenuEditOpen.value = true;
  }
};

const handleMealEdited = (data: {
  meal: Repas;
  extras: NonNullable<CartItem["extras"]>;
}) => {
  if (itemToEditId.value) {
    cartStore.updateCustomizedMeal(itemToEditId.value, data.extras);
    isMealEditOpen.value = false;
    itemToEditId.value = null;
  }
};

const handleMenuEdited = (data: {
  menu: Menu;
  customizations: NonNullable<CartItem["menuCustomizations"]>;
  sauces: NonNullable<CartItem["selectedSauces"]>;
  desserts: NonNullable<CartItem["selectedDesserts"]>;
}) => {
  if (itemToEditId.value) {
    cartStore.updateMenu(
      itemToEditId.value,
      data.customizations,
      data.sauces,
      data.desserts,
    );
    isMenuEditOpen.value = false;
    itemToEditId.value = null;
  }
};

// Group items by category
// Group items by category (Ordered)
const groupedItems = computed(() => {
  const groups: Record<string, CartItem[]> = {};

  cartStore.items.forEach((item) => {
    let categoryName = "Autres";

    if (item.type === "menu") {
      categoryName = "Menus";
    } else if (item.meal && item.meal.group) {
      categoryName = item.meal.group;
    } else if (item.meal && item.meal.category) {
      if (item.meal.category.name === "Boissons") categoryName = "Boissons";
      else if (item.meal.category.name === "Desserts")
        categoryName = "Desserts";
      else categoryName = "Repas";
    }

    if (!groups[categoryName]) {
      groups[categoryName] = [];
    }
    groups[categoryName].push(item);
  });

  // Define sort order
  const order = ["Menus", "Repas", "Boissons", "Desserts", "Autres"];

  return order
    .filter((key) => groups[key] && groups[key].length > 0)
    .map((key) => ({
      name: key,
      items: groups[key],
    }));
});

const updateQuantity = (item: CartItem, change: number) => {
  const newQty = item.quantity + change;
  if (newQty > 0) {
    cartStore.updateQuantity(item.id, newQty);
  }
};

// proceedToPayment removed as logic is handled in PaymentForm

// Sync delivery details with store
watch(
  deliveryDetails,
  (newVal) => {
    cartStore.setDeliveryInfo(newVal);
  },
  { deep: true },
);
</script>
